/**
 * Validation utilities for case fields
 *
 * Mirrors backend validation rules from shared/validation.py
 * to provide immediate user feedback before API submission.
 */

/**
 * Regular expression patterns for blocked characters
 */
export const BLOCKED_PATTERNS = {
  /** SQL comment patterns: -- and block comments */
  SQL_COMMENT: /--|\/\*|\*\//,
  /** SQL statement terminator */
  STATEMENT_TERMINATOR: /;/,
  /** HTML/XSS tag markers */
  HTML_TAGS: /[<>]/,
} as const;

/**
 * Error messages for validation failures
 */
export const VALIDATION_ERRORS = {
  CASE_NAME_REQUIRED: 'Case name is required',
  CASE_NAME_HTML_TAGS: 'Case name cannot contain < or >',
  CASE_NAME_SQL_COMMENT: 'Case name cannot contain -- or block comments',
  CASE_NAME_TERMINATOR: 'Case name cannot contain ;',
  CASE_NAME_TOO_LONG: 'Case name must be 255 characters or less',
  IDENTIFIER_SQL_COMMENT: 'cannot contain -- or block comments',
  IDENTIFIER_TERMINATOR: 'cannot contain ;',
  IDENTIFIER_TOO_LONG: 'must be 100 characters or less',
  DEFENDANT_NAME_TOO_LONG: 'Full name must be 100 characters or less',
} as const;

/**
 * Client-side cap for a defendant's full name (GitHub #803).
 *
 * The backend `DefendantUpdateRequest.full_name` accepts up to 255 characters,
 * but the product decision (issue #803) is to surface a stricter 100-character
 * limit in the UI with a live counter so the user never hits the backend's
 * opaque 422. This is the single source of truth shared by the input
 * `maxLength`, the character counter, and `validateDefendantFullName`.
 */
export const DEFENDANT_FULL_NAME_MAX_LENGTH = 100;

/**
 * Client-side cap for a defendant identifier (Booking Number, RNI Number,
 * and any other tenant identifier field). GitHub #1866.
 *
 * Matches `DefendantIdentifierPayload.value` (`max_length=500` in
 * `shared/validation.py`). Shared by the input `maxLength` and
 * `validateDefendantIdentifierValue` so the field stops where the
 * validator stops.
 */
export const DEFENDANT_IDENTIFIER_MAX_LENGTH = 500;

/**
 * Validates a case name field
 *
 * Rules:
 * - Required, non-empty after trimming
 * - Maximum 255 characters
 * - Cannot contain: < > ; -- or block comments
 * - Allows: apostrophes ('), quotes ("), ampersands (&)
 *
 * @param value - The case name to validate
 * @returns Error message string if validation fails, null if valid
 *
 * @example
 * validateCaseName("Austin's Case")  // returns null (valid)
 * validateCaseName("Case--Name")      // returns error message (SQL comment pattern)
 * validateCaseName("")                // returns error message (required)
 */
export function validateCaseName(value: string): string | null {
  if (!value.trim()) {
    return VALIDATION_ERRORS.CASE_NAME_REQUIRED;
  }
  if (BLOCKED_PATTERNS.HTML_TAGS.test(value)) {
    return VALIDATION_ERRORS.CASE_NAME_HTML_TAGS;
  }
  if (BLOCKED_PATTERNS.SQL_COMMENT.test(value)) {
    return VALIDATION_ERRORS.CASE_NAME_SQL_COMMENT;
  }
  if (BLOCKED_PATTERNS.STATEMENT_TERMINATOR.test(value)) {
    return VALIDATION_ERRORS.CASE_NAME_TERMINATOR;
  }
  if (value.length > 255) {
    return VALIDATION_ERRORS.CASE_NAME_TOO_LONG;
  }
  return null; // Valid
}

/**
 * Validates case identifier fields (case_num, booking_num, rni_num)
 *
 * Rules:
 * - Optional (null or empty values are valid)
 * - Maximum 100 characters
 * - Cannot contain: ; -- or block comments
 * - Allows: apostrophes ('), quotes ("), ampersands (&)
 *
 * @param value - The identifier value to validate (null/empty allowed)
 * @param fieldLabel - Human-readable field name for error messages (e.g., "Case number", "Booking number")
 * @returns Error message string if validation fails, null if valid
 *
 * @example
 * validateCaseIdentifier("2024-CR-12345", "Case number")  // returns null (valid)
 * validateCaseIdentifier(null, "Case number")             // returns null (optional)
 * validateCaseIdentifier("Case;123", "Case number")       // returns "Case number cannot contain ;"
 */
export function validateCaseIdentifier(
  value: string | null,
  fieldLabel: string
): string | null {
  if (!value) return null; // Optional fields

  if (BLOCKED_PATTERNS.SQL_COMMENT.test(value)) {
    return `${fieldLabel} ${VALIDATION_ERRORS.IDENTIFIER_SQL_COMMENT}`;
  }
  if (BLOCKED_PATTERNS.STATEMENT_TERMINATOR.test(value)) {
    return `${fieldLabel} ${VALIDATION_ERRORS.IDENTIFIER_TERMINATOR}`;
  }
  if (value.length > 100) {
    return `${fieldLabel} ${VALIDATION_ERRORS.IDENTIFIER_TOO_LONG}`;
  }

  return null; // Valid
}

/**
 * Validates a defendant full name field
 *
 * Rules:
 * - Required, non-empty after trimming
 * - Maximum DEFENDANT_FULL_NAME_MAX_LENGTH (100) characters (#803)
 * - Cannot contain: < > ; -- or block comments
 *
 * @param value - The defendant full name to validate
 * @returns Error message string if validation fails, null if valid
 */
export function validateDefendantFullName(value: string): string | null {
  if (!value.trim()) {
    return 'Full name is required';
  }
  if (BLOCKED_PATTERNS.HTML_TAGS.test(value)) {
    return 'Full name cannot contain < or >';
  }
  if (BLOCKED_PATTERNS.SQL_COMMENT.test(value)) {
    return 'Full name cannot contain -- or block comments';
  }
  if (BLOCKED_PATTERNS.STATEMENT_TERMINATOR.test(value)) {
    return 'Full name cannot contain ;';
  }
  if (value.length > DEFENDANT_FULL_NAME_MAX_LENGTH) {
    return VALIDATION_ERRORS.DEFENDANT_NAME_TOO_LONG;
  }
  return null;
}

/**
 * Validates a defendant identifier value (e.g., Booking Number, RNI Number)
 *
 * Rules:
 * - Optional (empty values are valid — they are skipped on submit)
 * - Maximum DEFENDANT_IDENTIFIER_MAX_LENGTH (500) characters (#1866)
 * - Cannot contain: < > ; -- or block comments
 *
 * @param value - The identifier value to validate
 * @param fieldLabel - Human-readable field name for error messages
 * @returns Error message string if validation fails, null if valid
 */
export function validateDefendantIdentifierValue(
  value: string,
  fieldLabel: string
): string | null {
  if (!value.trim()) return null;

  if (BLOCKED_PATTERNS.HTML_TAGS.test(value)) {
    return `${fieldLabel} cannot contain < or >`;
  }
  if (BLOCKED_PATTERNS.SQL_COMMENT.test(value)) {
    return `${fieldLabel} cannot contain -- or block comments`;
  }
  if (BLOCKED_PATTERNS.STATEMENT_TERMINATOR.test(value)) {
    return `${fieldLabel} cannot contain ;`;
  }
  if (value.length > DEFENDANT_IDENTIFIER_MAX_LENGTH) {
    return `${fieldLabel} must be ${DEFENDANT_IDENTIFIER_MAX_LENGTH} characters or less`;
  }
  return null;
}

/**
 * Password validation constants
 */
// Mirrors the server-side GCIP passwordPolicyConfig in
// infrastructure/terraform/modules/identity_platform/main.tf (IA-5(1)). The
// server is authoritative; keep these in sync so users see the rule before
// Firebase rejects the password.
export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 12,
  REQUIRE_UPPERCASE: true,
  REQUIRE_LOWERCASE: true,
  REQUIRE_NUMBER: true,
  REQUIRE_SPECIAL: true,
} as const;

// GCIP's containsNonAlphanumericCharacter only counts this documented set.
// Other characters (- + = space, non-ASCII letters) are allowed in a password
// but do not satisfy the symbol rule, so the server would reject it.
export const PASSWORD_SYMBOLS = '^$*.[]{}()?"!@#%&/\\,><\':;|_~`';

/**
 * Password validation error messages
 */
export const PASSWORD_ERRORS = {
  TOO_SHORT: `Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters`,
  NEEDS_UPPERCASE: 'Password must contain at least one uppercase letter',
  NEEDS_LOWERCASE: 'Password must contain at least one lowercase letter',
  NEEDS_NUMBER: 'Password must contain at least one number',
  NEEDS_SPECIAL: `Password must contain at least one of these symbols: ${PASSWORD_SYMBOLS.split('').join(' ')}`,
  MISMATCH: 'Passwords do not match',
} as const;

/**
 * Validates password strength requirements
 *
 * Requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one symbol from PASSWORD_SYMBOLS
 *
 * @param password - The password to validate
 * @returns Error message string if validation fails, null if valid
 *
 * @example
 * validatePassword("weak")              // returns "Password must be at least 12 characters"
 * validatePassword("weakpassword1!")    // returns "Password must contain at least one uppercase letter"
 * validatePassword("StrongPass123!")    // returns null (valid)
 */
export function validatePassword(password: string): string | null {
  if (password.length < PASSWORD_REQUIREMENTS.MIN_LENGTH) {
    return PASSWORD_ERRORS.TOO_SHORT;
  }

  if (PASSWORD_REQUIREMENTS.REQUIRE_UPPERCASE && !/[A-Z]/.test(password)) {
    return PASSWORD_ERRORS.NEEDS_UPPERCASE;
  }

  if (PASSWORD_REQUIREMENTS.REQUIRE_LOWERCASE && !/[a-z]/.test(password)) {
    return PASSWORD_ERRORS.NEEDS_LOWERCASE;
  }

  if (PASSWORD_REQUIREMENTS.REQUIRE_NUMBER && !/[0-9]/.test(password)) {
    return PASSWORD_ERRORS.NEEDS_NUMBER;
  }

  if (
    PASSWORD_REQUIREMENTS.REQUIRE_SPECIAL &&
    ![...password].some((char) => PASSWORD_SYMBOLS.includes(char))
  ) {
    return PASSWORD_ERRORS.NEEDS_SPECIAL;
  }

  return null; // Valid
}

/**
 * Validates password and confirmation match
 *
 * @param password - The password
 * @param confirmPassword - The confirmation password
 * @returns Error message string if validation fails, null if valid
 */
export function validatePasswordWithConfirm(
  password: string,
  confirmPassword: string
): string | null {
  // First check password strength
  const strengthError = validatePassword(password);
  if (strengthError) return strengthError;

  // Then check match
  if (password !== confirmPassword) {
    return PASSWORD_ERRORS.MISMATCH;
  }

  return null; // Valid
}

/**
 * Get human-readable password requirements text
 * @returns Requirements description for display
 */
export function getPasswordRequirementsText(): string {
  return `Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters with uppercase, lowercase, a number, and a symbol such as ! @ # $ % (- + = and spaces do not count)`;
}
